import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashRestore, faEye, faTimes, faPencilAlt, faTrash } from '@fortawesome/free-solid-svg-icons';
import { renderSortIcon } from '../../../utils/sortIconHelper';

// Helper function to format dates in user's local timezone
const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    // Display in user's local timezone
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch (e) {
    return 'Invalid Date';
  }
};

const FlightManagementTable = ({
  flights = [],
  loading = false,
  error = null,
  searchTerm = '',
  onSearchChange = () => { },
  statusFilter = '',
  onStatusFilterChange = null,
  recordsPerPage = 10,
  onRecordsPerPageChange = () => { },
  currentPage = 1,
  onPreviousPage = () => { },
  onNextPage = () => { },
  onPageChange = () => { },
  visibleColumns = {},
  airliners = [],
  onViewFlight = () => { },
  onEditFlight = null,
  onDeleteFlight = null,
  onCancelFlight = null,
  onRestoreFlight = () => { },
  onPermanentDeleteFlight = () => { },
  totalRecords: totalRecordsProp = 0,
  totalPages: totalPagesProp = 1,
  showActionColumn = true,
  sortConfig = { key: 'id', direction: 'desc' },
  onSortChange = () => { },
  hideTopControls = false
}) => {
  // Sorting handler - triggers server-side sort
  const handleSort = (key) => {
    onSortChange(key);
  };

  // Pagination calculations
  const totalRecords = totalRecordsProp || flights.length;
  const totalPages = totalPagesProp || Math.ceil(totalRecords / recordsPerPage) || 1;
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = Math.min(startIndex + recordsPerPage, totalRecords);
  const currentData = flights; // Data is already sorted and paginated from server

// Sorting is handled by trigger server-side sort via onSortChange prop

  // Debug logging
  console.log('🔄 FlightManagementTable Pagination:', {
    totalRecords,
    totalPages,
    currentPage,
    recordsPerPage,
    startIndex,
    endIndex,
    currentDataLength: currentData.length
  });

  // Debug airliner data
  if (currentData.length > 0) {
    console.log('🔍 First flight data:', currentData[0]);
    console.log('🔍 Airliners list:', airliners);
    console.log('🔍 Flight airliner_id:', currentData[0].airliner_id);
    console.log('🔍 Flight user_id:', currentData[0].user_id);
  }

  return (
    <div className="bg-white mt-[10px]">
      {!hideTopControls && (
        <div className="flex mx-[15px] justify-between items-center py-[15px] px-[15px] md:px-[30px] bg-transparent mb-[15px] flex-col md:flex-row gap-[15px] md:gap-0 items-start md:items-center">
          <div className="flex items-center gap-2 text-[13px] text-[#555] w-full md:w-auto justify-between md:justify-start">
            <span>Display</span>
            <select
              value={recordsPerPage}
              onChange={onRecordsPerPageChange}
              className="w-[75px] h-[34px] py-1.5 px-3 text-[13px] leading-[1.42857143] text-black bg-white border border-[#ccc] rounded-[5px] shadow-none font-semibold inline-block align-middle my-0 mx-[5px] transition-[border-color] duration-150 focus:outline-none focus:border-[#ccc] focus:shadow-none"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>Records</span>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2.5 m-0 w-full md:w-auto">
            {onStatusFilterChange && (
              <div className="flex items-center gap-2.5 w-full sm:w-auto">
                <label className="text-[13px] text-[#555] font-normal whitespace-nowrap">Status</label>
                <select
                  value={statusFilter}
                  onChange={onStatusFilterChange}
                  className="inline-block w-full sm:w-[160px] h-[34px] py-1.5 px-3 text-[13px] leading-[1.42857143] text-[#4e4c4c] bg-white border border-[#ccc] rounded-[5px] shadow-none align-middle appearance-none box-border transition-[border-color,box-shadow] duration-150 font-normal focus:outline-none focus:border-[#ccc] focus:shadow-none"
                >
                  <option value="">All Status</option>
                  <option value="published">Published</option>
                  <option value="saved">Saved</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            )}
            <div className="flex items-center gap-2.5 w-full sm:w-auto">
              <label className="text-[13px] text-[#555] font-normal">Search</label>
              <input
                type="text"
                placeholder="Flight #, Reg, Airport"
                value={searchTerm}
                onChange={onSearchChange}
                className="inline-block w-full sm:w-[180px] h-[34px] py-1.5 px-3 text-[13px] leading-[1.42857143] text-[#4e4c4c] bg-white border border-[#ccc] rounded-[5px] shadow-none align-middle appearance-none box-border transition-[border-color,box-shadow] duration-150 font-normal focus:outline-none focus:border-[#ccc] focus:shadow-none placeholder:text-[#999] placeholder:font-normal"
              />
            </div>
          </div>
        </div>
      )}

      <div className="bg-white mx-[15px] mt-0 mb-[15px] rounded-none shadow-none border border-[#ddd] overflow-x-auto max-w-full" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <table className="border-collapse min-w-[1000px] w-full">
          <thead className="bg-white border-b-0">
            <tr>
              <th className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider cursor-pointer hover:bg-gray-200 transition-colors" 
              // onClick={() => handleSort('id')} style={{ textTransform: 'none' }}
              >
                <div className="flex items-center">
                  # 
                  {/* {renderSortIcon('id', sortConfig.key, sortConfig.direction.toUpperCase())} */}
                </div>
              </th>
              {visibleColumns.regNo && (
                <th className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('registration_number')} style={{ textTransform: 'none' }}>
                  <div className="flex items-center">
                    Reg No. {renderSortIcon('registration_number', sortConfig.key, sortConfig.direction.toUpperCase())}
                  </div>
                </th>
              )}
              {visibleColumns.flightNo && (
                <th className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('flight_number')} style={{ textTransform: 'none' }}>
                  <div className="flex items-center">
                    Flight Number {renderSortIcon('flight_number', sortConfig.key, sortConfig.direction.toUpperCase())}
                  </div>
                </th>
              )}
              {visibleColumns.airliner && (
                <th className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('airliner_id')} style={{ textTransform: 'none' }}>
                  <div className="flex items-center">
                    Airliner {renderSortIcon('airliner_id', sortConfig.key, sortConfig.direction.toUpperCase())}
                  </div>
                </th>
              )}
              {visibleColumns.depAirport && (
                <th onClick={() => handleSort('departure_airport')} className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider cursor-pointer hover:bg-gray-200 transition-colors" style={{ textTransform: 'none' }}>
                  <div className="flex items-center">
                    Dep. Airport {renderSortIcon('departure_airport', sortConfig.key, sortConfig.direction.toUpperCase())}
                  </div>
                </th>
              )}
              {visibleColumns.etd && (
                <th className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('departure_date_time')} style={{ textTransform: 'none' }}>
                  <div className="flex items-center">
                    Depart Date Time {renderSortIcon('departure_date_time', sortConfig.key, sortConfig.direction.toUpperCase())}
                  </div>
                </th>
              )}
              {visibleColumns.arrAirport && (
                <th onClick={() => handleSort('arrival_airport')} className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider cursor-pointer hover:bg-gray-200 transition-colors" style={{ textTransform: 'none' }}>
                  <div className="flex items-center">
                    Arr. Airport {renderSortIcon('arrival_airport', sortConfig.key, sortConfig.direction.toUpperCase())}
                  </div>
                </th>
              )}
              {visibleColumns.eta && (
                <th className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('arrival_date_time')} style={{ textTransform: 'none' }}>
                  <div className="flex items-center">
                    ETA {renderSortIcon('arrival_date_time', sortConfig.key, sortConfig.direction.toUpperCase())}
                  </div>
                </th>
              )}
              {visibleColumns.belPrice && (
                <th className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('block_empty_price')} style={{ textTransform: 'none' }}>
                  <div className="flex items-center">
                    BEL Price {renderSortIcon('block_empty_price', sortConfig.key, sortConfig.direction.toUpperCase())}
                  </div>
                </th>
              )}
              {visibleColumns.blPrice && (
                <th className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('block_price')} style={{ textTransform: 'none' }}>
                  <div className="flex items-center">
                    EL Price {renderSortIcon('block_price', sortConfig.key, sortConfig.direction.toUpperCase())}
                  </div>
                </th>
              )}
              {visibleColumns.seatPrice && (
                <th className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('seat_price')} style={{ textTransform: 'none' }}>
                  <div className="flex items-center">
                    Seat Price {renderSortIcon('seat_price', sortConfig.key, sortConfig.direction.toUpperCase())}
                  </div>
                </th>
              )}
              {visibleColumns.seatTotal && (
                <th className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('seat_total')} style={{ textTransform: 'none' }}>
                  <div className="flex items-center">
                    Total Seat {renderSortIcon('seat_total', sortConfig.key, sortConfig.direction.toUpperCase())}
                  </div>
                </th>
              )}
              {visibleColumns.seatAvailable && (
                <th className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('seat_available')} style={{ textTransform: 'none' }}>
                  <div className="flex items-center">
                    Available Seat {renderSortIcon('seat_available', sortConfig.key, sortConfig.direction.toUpperCase())}
                  </div>
                </th>
              )}
              {visibleColumns.flightTime && (
                <th className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('flight_time')} style={{ textTransform: 'none' }}>
                  <div className="flex items-center">
                    Flt Time {renderSortIcon('flight_time', sortConfig.key, sortConfig.direction.toUpperCase())}
                  </div>
                </th>
              )}
              {visibleColumns.status && (
                <th className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('status')} style={{ textTransform: 'none' }}>
                  <div className="flex items-center">
                    Status {renderSortIcon('status', sortConfig.key, sortConfig.direction.toUpperCase())}
                  </div>
                </th>
              )}
              {showActionColumn && visibleColumns.action && <th className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider cursor-pointer hover:bg-gray-200 transition-colors" style={{ textTransform: 'none' }}><span className="flex items-center gap-1">Action</span></th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={Object.values(visibleColumns).filter(Boolean).length + 1} className="text-center p-5 text-[#999] text-[13px]">
                  Loading flights...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={Object.values(visibleColumns).filter(Boolean).length + 1} className="text-center p-5 text-red-500 text-[13px]">
                  Error: {error}
                </td>
              </tr>
            ) : currentData.length === 0 ? (
              <tr>
                <td colSpan={Object.values(visibleColumns).filter(Boolean).length + 1} className="text-center p-5 text-[#999] text-[13px]">
                  No data available in table
                </td>
              </tr>
            ) : (
              currentData.map((flight, index) => {
                const statusValue = (flight.status || '').toLowerCase();
                const notOperateFlight = Number(flight.not_operate_flight);
                const bookingCount = Number(flight.booking_count);
                const isActive = statusValue === 'published' && notOperateFlight !== 1;
                const isDNO = statusValue === 'published' && notOperateFlight === 1 && bookingCount === 0;
                const isCompleted = statusValue === 'published' && notOperateFlight === 1 && bookingCount > 0;
                const isCancelled = statusValue === 'canceled' || statusValue === 'cancelled';
                const isSavedOrDraft = statusValue === 'saved' || statusValue === 'draft';
                const seatTotal = Number(flight.seat_total || 0);
                const seatAvailable = Number(flight.seat_available || 0);

                return (
                  <tr key={flight.id} className="hover:bg-white">
                    <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">{flight.index_id}</td>
                    {visibleColumns.regNo && <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">{flight.registration_number || flight.aircraft?.registration_number || 'N/A'}</td>}
                    {visibleColumns.flightNo && <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">{flight.flight_number || 'N/A'}</td>}
                    {visibleColumns.airliner && <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">{(() => {
                      // First priority: Get from nested aircraft.user object (from API response)
                      if (flight.aircraft?.user) {
                        const fullName = [flight.aircraft.user.first_name, flight.aircraft.user.last_name].filter(Boolean).join(' ').trim();
                        if (fullName) return fullName;
                        return flight.aircraft.user.company_name || flight.aircraft.user.email || '-';
                      }
                      // Second priority: lookup airliner by airliner_id or user_id from airliners list
                      const airlinerId = flight.airliner_id || flight.user_id || flight.aircraft?.user_id;
                      if (airlinerId && airliners && airliners.length > 0) {
                        const airliner = airliners.find(a => Number(a.id) === Number(airlinerId));
                        if (airliner) {
                          const fullName = [airliner.first_name, airliner.last_name].filter(Boolean).join(' ').trim();
                          return fullName || airliner.company_name || airliner.email || '-';
                        }
                      }
                      // Third priority: Check for flat airliner_name field
                      if (flight.airliner_name) {
                        return flight.airliner_name;
                      }
                      // Fourth priority: Try to get airliner from flight.airliner relationship
                      if (flight.airliner) {
                        const fullName = [flight.airliner.first_name, flight.airliner.last_name].filter(Boolean).join(' ').trim();
                        return fullName || flight.airliner.company_name || flight.airliner.email || '-';
                      }
                      return '-';
                    })()}</td>}
                    {visibleColumns.depAirport && <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">{flight.dep_airport || flight.departureAirport?.iata_code || 'N/A'}</td>}
                    {visibleColumns.etd && <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">{formatDateTime(flight.departure_date_time)}</td>}
                    {visibleColumns.arrAirport && <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">{flight.arr_airport || flight.arrivalAirport?.iata_code || 'N/A'}</td>}
                    {visibleColumns.eta && <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">{formatDateTime(flight.arrival_date_time)}</td>}
                    {visibleColumns.belPrice && <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">{Number(flight.block_empty_price) > 0 ? `$${flight.block_empty_price}` : '0'}</td>}
                    {visibleColumns.blPrice && <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">{Number(flight.block_price) > 0 ? `$${flight.block_price}` : '0'}</td>}
                    {visibleColumns.seatPrice && <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">{Number(flight.seat_price) > 0 ? `$${flight.seat_price}` : '0'}</td>}
                    {visibleColumns.seatTotal && <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">{Number(flight.seat_total) === 0 ? '-' : flight.seat_total}</td>}
                    {visibleColumns.seatAvailable && <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">{Number(flight.seat_total) === 0 ? '-' : (flight.seat_available || 0)}</td>}
                    {visibleColumns.flightTime && <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">{flight.flight_time || 'N/A'}</td>}
                    {visibleColumns.status && <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0 text-center">
                      {flight.deleted_at ? (
                        <span className="inline-block py-1 px-2 text-xs font-semibold text-white rounded" style={{ backgroundColor: '#3c8dbc' }}>Deleted</span>
                      ) : flight.status === 'published' ? (
                        flight.not_operate_flight === 1 ? (
                          flight.booking_count === 0 ? (
                            <span className="inline-block py-1 px-2 text-xs font-semibold text-white bg-gray-500 rounded">DNO</span>
                          ) : (
                            <span className="inline-block py-1 px-2 text-xs font-semibold text-white bg-blue-500 rounded">Completed</span>
                          )
                        ) : (
                          <span className="inline-block py-1 px-2 text-xs font-semibold text-white bg-green-500 rounded">Active</span>
                        )
                      ) : flight.status === 'canceled' || flight.status === 'cancelled' ? (
                        <span className="inline-block py-1 px-2 text-xs font-semibold text-white bg-red-500 rounded">Cancelled</span>
                      ) : flight.status === 'draft' || flight.status === 'saved' ? (
                        <span className="inline-block py-1 px-2 text-xs font-semibold text-white bg-orange-500 rounded">Saved</span>
                      ) : (
                        <span className="inline-block py-1 px-2 text-xs font-semibold text-white bg-gray-500 rounded">{flight.status || 'N/A'}</span>
                      )}
                    </td>}
                    {showActionColumn && visibleColumns.action && <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0 text-end">
                      {flight.deleted_at ? (
                        <div className="flex gap-1 justify-end">
                          <button
                            onClick={() => onRestoreFlight(flight.id)}
                            className="w-6 h-6 flex items-center justify-center bg-[#3b5998] text-white rounded hover:bg-[#2d4373] transition-colors"
                            title="Restore Flight"
                          >
                            <FontAwesomeIcon icon={faTrashRestore} className="text-[9px]" />
                          </button>
                          <button
                            onClick={() => onPermanentDeleteFlight(flight.id)}
                            className="w-6 h-6 flex items-center justify-center bg-[#dd4b39] text-white rounded hover:bg-[#c0392b] transition-colors"
                            title="Permanently Delete"
                          >
                            <FontAwesomeIcon icon={faTrash} className="text-[9px]" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-1 justify-end">
                          {/* View Button - Always visible */}
                          <button
                            onClick={() => onViewFlight(flight.id)}
                            className="w-6 h-6 flex items-center justify-center bg-[#3b5998] text-white rounded hover:bg-[#2d4373] transition-colors"
                            title="Show Flight Details"
                          >
                            <FontAwesomeIcon icon={faEye} className="text-[9px]" />
                          </button>

                          {/* Edit Button - Allowed for (Active with no bookings) OR DNO OR Saved/Draft OR Cancelled */}
                          {onEditFlight && ((isActive && seatTotal === seatAvailable) || isDNO || isSavedOrDraft || isCancelled) && (
                            <button
                              onClick={() => onEditFlight(flight.id)}
                              className="w-6 h-6 flex items-center justify-center bg-[#f4c430] text-black rounded hover:bg-[#e0b020] transition-colors"
                              title="Edit Flight Details"
                            >
                              <FontAwesomeIcon icon={faPencilAlt} className="text-[9px]" />
                            </button>
                          )}

                          {/* Delete Button - Allowed for DNO, Cancelled(no bookings), Saved/Draft, OR Completed */}
                          {onDeleteFlight && (isDNO || (isCancelled && bookingCount === 0) || isSavedOrDraft || isCompleted) && (
                            <button
                              onClick={() => onDeleteFlight(flight.id)}
                              className="w-6 h-6 flex items-center justify-center bg-[#dd4b39] text-white rounded hover:bg-[#c0392b] transition-colors"
                              title="Delete Flight"
                            >
                              <FontAwesomeIcon icon={faTrash} className="text-[9px]" />
                            </button>
                          )}

                          {/* Cancel Button - Allowed for Published (Active) flights that are NOT yet Cancelled */}
                          {onCancelFlight && (isActive && !isCancelled && !isSavedOrDraft) && (
                            <button
                              onClick={() => onCancelFlight(flight.id)}
                              className="w-6 h-6 flex items-center justify-center bg-[#dd4b39] text-white rounded hover:bg-[#c0392b] transition-colors"
                              title="Cancel Flight"
                            >
                              <FontAwesomeIcon icon={faTimes} className="text-[9px]" />
                            </button>
                          )}
                        </div>
                      )}
                    </td>}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center mx-[15px] md:mx-[30px] my-0 py-[15px] px-0 bg-transparent gap-2 flex-nowrap">
        <div className="text-[13px] text-[#555] text-left whitespace-nowrap flex-shrink min-w-0 overflow-hidden text-ellipsis">
          Showing {totalRecords === 0 ? 0 : startIndex + 1} to {endIndex} of {totalRecords} entries
        </div>
        <div className="inline-flex flex-shrink-0 overflow-x-auto">
          <button
            onClick={onPreviousPage}
            className="py-[5px] px-4 border border-[#ddd] bg-white rounded-tl-md rounded-bl-md rounded-tr-none rounded-br-none border-r-0 cursor-pointer text-base text-[#aaa] h-[34px] min-w-[80px] sm:min-w-[100px] font-light inline-block align-middle appearance-none box-border transition-all duration-200 leading-[1.5] text-center whitespace-nowrap select-none shadow-[0_1px_3px_rgba(0,0,0,0.05)] hover:bg-[#f9f9f9] hover:border-[#ccc] hover:text-[#888] focus:outline-none focus:border-[#ddd] focus:shadow-[0_1px_3px_rgba(0,0,0,0.05)]"
          >
            Previous
          </button>

          {/* Page Number Buttons */}
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
            <button
              key={pageNum}
              onClick={() => {
                console.log('🔄 Page button clicked:', pageNum);
                if (onPageChange) {
                  onPageChange(pageNum);
                }
              }}
              className={`py-[5px] px-3 border border-[#ddd] cursor-pointer text-base h-[34px] min-w-[40px] font-light inline-block align-middle appearance-none box-border transition-all duration-200 leading-[1.5] text-center whitespace-nowrap select-none shadow-[0_1px_3px_rgba(0,0,0,0.05)] ${pageNum === currentPage
                ? 'bg-[#ffeb00] text-black border-[#ffeb00] font-semibold'
                : 'bg-white text-[#aaa] hover:bg-[#f9f9f9] hover:border-[#ccc] hover:text-[#888]'
                } focus:outline-none focus:border-[#ddd] focus:shadow-[0_1px_3px_rgba(0,0,0,0.05)]`}
            >
              {pageNum}
            </button>
          ))}

          <button
            onClick={onNextPage}
            className="py-[5px] px-4 border border-[#ddd] bg-white rounded-tr-md rounded-br-md rounded-tl-none rounded-bl-none cursor-pointer text-base text-[#aaa] h-[34px] min-w-[80px] sm:min-w-[100px] font-light inline-block align-middle appearance-none box-border transition-all duration-200 leading-[1.5] text-center whitespace-nowrap select-none shadow-[0_1px_3px_rgba(0,0,0,0.05)] hover:bg-[#f9f9f9] hover:border-[#ccc] hover:text-[#888] focus:outline-none focus:border-[#ddd] focus:shadow-[0_1px_3px_rgba(0,0,0,0.05)]"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlightManagementTable;
